<script lang="ts">
  import * as _ from "lodash";
  import PageWrapper from "@retailer/svelte/Shared/PageWrapper.svelte";
  import ShareSellSheetModal from "@distributor/svelte/SellSheet/Components/ShareSellSheetModal.svelte";
  import CopySellSheetModal from "@distributor/svelte/SellSheet/Components/CopySellSheetModal.svelte";
  import ProductsWrapper from "@distributor/svelte/SellSheet/Components/ProductsWrapper.svelte";
  import Sidebar from "@distributor/svelte/SellSheet/Components/Sidebar.svelte";
  import type { DistributorSellSheet } from "@shared/types/SellSheet";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import Authorization from "@shared/svelte/Components/Authorization.svelte";
  import Arrow from "@shared/svelte/Components/Arrow.svelte";
  import FormErrors from "@shared/svelte/Components/FormErrors.svelte";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { goto } from "@roxi/routify";

  export let sellSheet: DistributorSellSheet;

  let errors = null;
  let page = {
    editing: false,
    productLineSearch: null,
    searching: false,
    loading: false,
    searchResults: [],
    plForm: {},
    loadingProducts: false,
    showSearch: true,
    showShares: false,
    showInfo: true,
    sellSheetCopy: _.cloneDeep(sellSheet),
    showMetrics: false,
    selectedMetricsTab: "views",
    showAddProductsModal: false,
    productTypeFilter: null,
    totalViewsSum: 0,
    productsPurchasedSum: 0,
    showCopyModal: false,
  };
  page.totalViewsSum = _.sum(
    _.map(sellSheet.retailer_views, function (x) {
      return x.non_unique_views;
    }),
  );
  page.productsPurchasedSum = _.sum(
    _.map(sellSheet.retailer_purchases, function (x) {
      return x.products_purchased;
    }),
  );

  let showShareSellSheetModal = false;
  let retailerUserCount = sellSheet.retailer_count || 0;
  let showShareWithRepsButton = !stagedRolloutCheck("hide_product_sheet_share_with_reps");

  if (!sellSheet.id || sellSheet.name == "Untitled Sheet") {
    page.editing = true;
  }

  const closeModals = function () {
    showShareSellSheetModal = false;
    page.showCopyModal = false;
  };

  const preview = function () {
    return $goto("/distributor/product_sheets/preview/:productSheetId", {
      productSheetId: sellSheet.id,
    });
  };

  const duplicate = function () {
    page.showCopyModal = true;
  };

  const deleteSheet = async function () {
    if (page.loading) {
      return;
    }
    page.loading = true;
    try {
      await HttpPromise({
        method: "DELETE",
        url: "/api/distributor/sell_sheets/" + sellSheet.id,
      });
      Flash.now({ message: "Product Sheet Deleted", type: "success" });
      window.location.href = "/distributor/product_sheets";
    } catch (error) {
      errors = error;
      page.loading = false;
    }
  };

  const share = function () {
    showShareSellSheetModal = true;
  };

  const shareWithAll = async function () {
    closeModals();
    page.loading = true;
    try {
      await HttpPromise({
        method: "POST",
        url: "/api/distributor/sell_sheets/share_with_all_retailers",
        data: {
          sell_sheet_id: sellSheet.id,
        },
      });
      let flashMethod = window["modern_mode"] ? Flash.now : Flash.queue;
      flashMethod({
        message: "Your product sheet has been successfully shared with selected retailers",
        type: "success",
      });
      window["stateService"].reload();
    } catch (error) {
      errors = error;
      page.loading = false;
    }
  };

  const shareWithSelected = async function (retailerIds) {
    closeModals();
    page.loading = true;
    try {
      await HttpPromise({
        method: "POST",
        url: "/api/distributor/sell_sheets/share_with_selected_retailers",
        data: {
          sell_sheet_id: sellSheet.id,
          retailer_users: retailerIds,
        },
      });
      let flashMethod = window["modern_mode"] ? Flash.now : Flash.queue;
      flashMethod({
        message: "Your product sheet has been successfully shared with selected retailers",
        type: "success",
      });
      window["stateService"].reload();
    } catch (error) {
      errors = error;
      page.loading = false;
    }
  };

  let retailersLoaded = false;
  const searchRetailers = async function (distributorId, searchTerm) {
    try {
      let res = await HttpPromise({
        method: "GET",
        url: `/api/distributor/sell_sheets/${sellSheet.id}/retailers`,
        data: {
          search_term: searchTerm,
          distributor_id: distributorId,
        },
      });
      retailersLoaded = true;
      page.loading = false;
      return res;
    } catch (error) {
      errors = error;
      page.loading = false;
      return Promise.resolve;
    }
  };
</script>

<style>
  .fizz-styles button {
    display: unset;
  }
</style>

<PageWrapper let:mobileMode useBackgroundWhite={false}>
  <div class="distributor-page">
    <div class="background-white">
      <div class="grid-container">
        <div class="grid-row align--middle padding-top--smaller padding-bottom--smaller">
          <div class="grid-col c12-12">
            <span class="default-subheader--bold">
              <Authorization authName={"sell_sheets.index"}>
                <UIRouterLink stateName="layout.product_sheets">Product Sheets</UIRouterLink>
                <Arrow
                  direction={"right"}
                  extraClass="color-grayDark margin-left--smallest margin-right--smallest"
                />
              </Authorization>
              {page.sellSheetCopy.name || "Untitled Sheet"}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <FormErrors {errors} />

  {#if mobileMode}
    <div class="grid-col c12-12">
      <div class="default-title--bold padding-top--small align-center"
        >{sellSheet.product_lines.length || 0} Product{#if sellSheet.product_lines.length != 1}s{/if}
        in Sheet</div
      >
      <div class="align-center padding-top--smaller fizz-styles">
        <button
          class="fizz-button-danger fizz-button-small button--secondary margin-right--smallest button--alert delete-link"
          on:click={deleteSheet}>Delete</button
        >
        <button
          class="fizz-button fizz-button-small button--secondary margin-right--smallest preview-link"
          on:click={preview}>Preview</button
        >
        {#if showShareWithRepsButton}
          <button
            class="fizz-button fizz-button-small button--primary margin-right--smallest duplicate-link"
            on:click={duplicate}>Share with Reps</button
          >
        {/if}
        <button
          class="fizz-button-primary fizz-button-small button--primary share-link"
          on:click={share}>Share with Retailers</button
        >
      </div>
    </div>
  {/if}

  <div class="grid-container margin-top--small">
    <div class="grid-row">
      <Sidebar {sellSheet} {page} {mobileMode} />
      <div
        class="grid-col c12-12 c9-12--md {mobileMode
          ? 'padding-left--none padding-right--none'
          : ''}"
      >
        <div
          class="cart__item-header align-left padding--small padding--smaller--md tablet:block background-white"
        >
          <div class="grid-row">
            <!--Desktop Search Label -->
            {#if !mobileMode}
              <div class="flex justify-between px-10 gap-16">
                <span
                  class="default-body--bold default-title--bold--md padding-top--smaller--md padding-left--smaller--md vertical-align-middle float-left"
                  style="margin-top: -2px;"
                  >{sellSheet.product_lines.length || 0} Product{#if sellSheet.product_lines.length != 1}s{/if}
                  in Sheet</span
                >
                <span class="sell-sheet-button-wrapper flex flex-wrap gap-8 fizz-styles">
                  <button
                    class="fizz-button-danger button--secondary margin-right--smallest button--alert delete-link"
                    on:click={deleteSheet}>Delete</button
                  >
                  <button
                    class="fizz-button button--secondary margin-right--smallest preview-link"
                    on:click={preview}>Preview</button
                  >
                  {#if showShareWithRepsButton}
                    <button
                      class="fizz-button button--primary margin-right--smallest duplicate-link"
                      on:click={duplicate}>Share with Reps</button
                    >
                  {/if}
                  <button class="fizz-button-primary button--primary share-link" on:click={share}
                    >Share with Retailers</button
                  >
                </span>
              </div>
            {/if}
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="grid-container margin-bottom--small">
    <div class="grid-row">
      <div
        class="grid-col c12-12 c9-12--md {mobileMode
          ? 'padding-left--none padding-right--none'
          : ''}"
      >
        <div class="background-white">
          <ProductsWrapper bind:sellSheet {mobileMode} bind:page />
        </div>
      </div>
    </div>
  </div>

  {#if page.showCopyModal}
    <span class="fizz-styles">
      <CopySellSheetModal
        {sellSheet}
        showCopyModal={page.showCopyModal}
        onCloseModal={closeModals}
      />
    </span>
  {/if}

  <span class="fizz-styles">
    <ShareSellSheetModal
      {showShareSellSheetModal}
      {shareWithAll}
      {shareWithSelected}
      closeModal={closeModals}
      {sellSheet}
      {retailerUserCount}
      {retailersLoaded}
      {searchRetailers}
      showLoading={false}
    />
  </span>
</PageWrapper>
